import { HttpClientModule } from '@angular/common/http';
import { NgModule, Optional, Provider, SkipSelf } from '@angular/core';
import { CoreModule as VendastaCoreModule, SessionService } from '@galaxy/core';
import { MarketplaceAppService as AppService } from '@galaxy/marketplace-apps/v1';
import { PartnerServiceInterfaceToken } from '@galaxy/partner';
import { ProductAnalyticsModule } from '@vendasta/product-analytics';
import { LocalRefreshingCacheService, LocalStorageService } from '@vendasta/uikit';
import { AccountsApiService } from './accounts-api/accounts.api.service';
import { AccountService } from './accounts.service';
import { ActivationEventService } from './activation-event.service';
import { AddonService } from './addon.service';
import { AuthApiService } from './auth/auth.api.service';
import { AuthService } from './auth/auth.service';
import { CategoryService } from './category.service';
import { ContactApiService } from './contact.api.service';
import { EditionDiffService } from './edition-diff/edition-diff.service';
import { EditionMarketingDiffService } from './edition-marketing-diff/edition-marketing-diff.service';
import { FeatureFlipperService } from './feature-flipper.service';
import { FileGroupsApiService } from './file-group/file-groups.api.service';
import { FileGroupsService } from './file-group/file-groups.service';
import { FileUploadService } from './file-uploader.service';
import { GalaxyImageUploadService } from './galaxy-image-upload.service';
import { HotJarService } from './hotjar.service';
import { ImageUploadService } from './image-upload.service';
import { MarketplaceAppDiffService } from './marketplace-app-diff/marketplace-app-diff.service';
import { MarketplaceAppService } from './marketplace-app/marketplace-app.service';
import { OrderFormSubmissionApiService } from './order-form-submission/order-form-submission.api.service';
import { OrderFormSubmissionService } from './order-form-submission/order-form-submission.service';
import { PartnerService } from './partner.service';
import { AccountDetailsGuard } from './routing-guards/account-details-guard';
import { EditFormCanDeactivateGuard } from './routing-guards/edit-form-can-deactivate-guard.service';
import { InboxRoutingGuard } from './routing-guards/inbox-routing-guard';
import { LoginRoutingGuard } from './routing-guards/login-routing-guard';
import { MarketingPreviewAndEditGuard } from './routing-guards/preview-edit-can-deactivate-guard';
import { ResellersTabRoutingGuard } from './routing-guards/resellers-tab-routing-guard';
import { ShowLoginRouteGuard } from './routing-guards/show-login-routing-guard';
import { CanMatchCrm } from './routing-guards/can-match-crm';
import { TestingApiService } from './testing/testing.api.service';

let CONFIG_PROVIDERS: Provider[] = [
  { provide: 'AccountsAPIHost', useValue: '//accounts-api-demo.vendasta-internal.com' },
  { provide: 'DeveloperCentralAPIHost', useValue: '//vendors-demo.vendasta.com' },
];

declare const window: any;

if (window.environment === 'prod') {
  CONFIG_PROVIDERS = [
    { provide: 'AccountsAPIHost', useValue: '//accounts-api-prod.vendasta-internal.com' },
    { provide: 'DeveloperCentralAPIHost', useValue: '//vendors.vendasta.com' },
  ];
}

const MODULES = [VendastaCoreModule, HttpClientModule, ProductAnalyticsModule];

const PROVIDING_SERVICES = [
  ...CONFIG_PROVIDERS,
  { provide: PartnerServiceInterfaceToken, useExisting: PartnerService },
  PartnerService,
  LocalStorageService,
  LocalRefreshingCacheService,
  FeatureFlipperService,
  SessionService,
  AuthService,
  AuthApiService,
  MarketplaceAppService,
  TestingApiService,
  HotJarService,
  MarketplaceAppDiffService,
  AppService,
  AddonService,
  FileUploadService,
  ImageUploadService,
  GalaxyImageUploadService,
  EditFormCanDeactivateGuard,
  MarketingPreviewAndEditGuard,
  ContactApiService,
  ActivationEventService,
  AccountsApiService,
  AccountService,
  FileGroupsService,
  FileGroupsApiService,
  OrderFormSubmissionService,
  OrderFormSubmissionApiService,
  AccountDetailsGuard,
  LoginRoutingGuard,
  InboxRoutingGuard,
  ResellersTabRoutingGuard,
  CanMatchCrm,
  ShowLoginRouteGuard,
  EditionMarketingDiffService,
  EditionDiffService,
  CategoryService,
];

@NgModule({
  imports: MODULES,
  declarations: [],
  providers: PROVIDING_SERVICES,
  exports: [VendastaCoreModule, HttpClientModule],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    // CoreModule should only be imported in Root module.
    throwIfAlreadyLoaded(parentModule, 'CoreModule');
  }
}

function throwIfAlreadyLoaded(parentModule: any, moduleName: string): void {
  if (parentModule) {
    throw new Error(`${moduleName} has already been loaded. Import Core modules in the AppModule only.`);
  }
}
